import { useTranslation } from "react-i18next";
import { MailOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import { createStyles } from "antd-style";

import { Button } from "@av/components/atoms/Button";
import { Input } from "@av/components/atoms/input/Input";
import { Typography } from "@av/components/atoms/typography/Typography";

export type MagicLinkConfirmFlowState =
  | "confirm"
  | "loggingIn"
  | "error"
  | "expired"
  | "rateLimited";

type MagicLinkConfirmFlowProps = {
  state: MagicLinkConfirmFlowState;
  email: string | null;
  expiresIn: number;
  /** Minutes the edge rate limit asks the user to wait; only used by the rateLimited state. */
  retryAfterMinutes?: number;
  onContinue: () => void;
  onRequestNewLink: () => void;
  isRequestingNewLink: boolean;
};

const useStyles = createStyles(({ token, css }) => ({
  card: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${token.marginLG}px;
    width: 440px;
    padding: ${token.paddingXL}px;
    background: ${token.colorBgContainer};
    border: 1px solid ${token.colorBorderSecondary};
    border-radius: ${token.borderRadiusLG}px;
    box-shadow: ${token.boxShadowTertiary};
  `,
  iconBadge: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 28px;
    font-size: 24px;
  `,
  iconBadgeInfo: css`
    background: ${token.colorInfoBg};
    color: ${token.colorInfo};
  `,
  iconBadgeWarning: css`
    background: ${token.colorWarningBg};
    color: ${token.colorWarning};
  `,
  message: css`
    margin-bottom: 0;
    text-align: center;
    color: ${token.colorTextSecondary};
  `,
  email: css`
    width: 100%;
    text-align: center;
  `,
  actions: css`
    width: 100%;
  `,
}));

const STATE_CONTENT: Record<
  MagicLinkConfirmFlowState,
  { icon: "mail" | "reload" | "warning"; titleKey: string; messageKey: string }
> = {
  confirm: {
    icon: "mail",
    titleKey: "auth.login.magicLinkLanding_confirmTitle",
    messageKey: "auth.login.magicLinkLanding_confirmMessage",
  },
  loggingIn: {
    icon: "reload",
    titleKey: "auth.login.magicLinkLanding_loggingYouIn",
    messageKey: "auth.login.magicLinkLanding_confirmMessage",
  },
  error: {
    icon: "warning",
    titleKey: "auth.login.magicLinkLanding_errorTitle",
    messageKey: "auth.login.magicLinkLanding_errorMessage",
  },
  expired: {
    icon: "warning",
    titleKey: "auth.login.magicLinkLanding_invalidTitle",
    messageKey: "auth.login.magicLinkLanding_invalidMessage",
  },
  rateLimited: {
    icon: "warning",
    titleKey: "auth.login.magicLinkLanding_rateLimitedTitle",
    messageKey: "auth.login.common_tooManyAttempts",
  },
};

function StateIcon({ icon }: { icon: "mail" | "reload" | "warning" }) {
  const { styles, cx } = useStyles();

  if (icon === "warning") {
    return (
      <div className={cx(styles.iconBadge, styles.iconBadgeWarning)}>
        <WarningOutlined />
      </div>
    );
  }

  return (
    <div className={cx(styles.iconBadge, styles.iconBadgeInfo)}>
      {icon === "mail" ? <MailOutlined /> : <ReloadOutlined spin />}
    </div>
  );
}

export function MagicLinkConfirmFlow({
  state,
  email,
  expiresIn,
  retryAfterMinutes,
  onContinue,
  onRequestNewLink,
  isRequestingNewLink,
}: MagicLinkConfirmFlowProps) {
  const { t } = useTranslation();
  const { styles } = useStyles();
  const content = STATE_CONTENT[state];

  return (
    <div className={styles.card} data-testid="magic-link-confirm-flow" data-state={state}>
      <StateIcon icon={content.icon} />

      <Typography.Title testIdPrefix="magic-link-confirm-flow" level={4}>
        {t(content.titleKey)}
      </Typography.Title>

      <Typography.Paragraph testIdPrefix="magic-link-confirm-flow" className={styles.message}>
        {t(content.messageKey, {
          count: state === "rateLimited" ? retryAfterMinutes : Math.ceil(expiresIn / 60_000),
        })}
      </Typography.Paragraph>

      {email && (
        <Input
          testIdPrefix="magic-link-confirm-flow-email"
          className={styles.email}
          value={email}
          disabled
        />
      )}

      <Flex vertical gap="small" className={styles.actions}>
        {/* The link is still valid when rate limited, so Continue doubles as the retry action. */}
        {(state === "confirm" || state === "loggingIn" || state === "rateLimited") && (
          <Button
            testIdPrefix="magic-link-confirm-flow-continue"
            type="primary"
            size="large"
            block
            loading={state === "loggingIn"}
            disabled={state === "loggingIn"}
            onClick={onContinue}
          >
            {t("auth.login.magicLinkLanding_continueButton")}
          </Button>
        )}

        {(state === "error" || state === "expired") && (
          <Button
            testIdPrefix="magic-link-confirm-flow-new-link"
            type="primary"
            size="large"
            block
            loading={isRequestingNewLink}
            disabled={isRequestingNewLink}
            onClick={onRequestNewLink}
          >
            {t("auth.login.magicLinkLanding_newLinkButton")}
          </Button>
        )}
      </Flex>
    </div>
  );
}
